<template>
  <div class="contract-table table-component">

    <file-uploader :show-preview="false" @onItemsAdd="onItemsAdd" :items-prop="files" :hint-text="false"
                   :uploading-progress="uploadingProgress" :uploading-progress-value="uploadingProgressValue"
                   :accept="fileUploaderAccept" ref="fileUploader" v-permission="changePermission" class="mb-2"/>


    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :checkboxes="true" :search="tableData.search"
                   :enable-simple-search="false" export-vuex-module-name="contracts" :enable-frontend-export="false"
                   :export-permission-name="exportPermission" :export-attributes="exportAttributes"
                   @paramsChanged="getTableData" filter-toggle-string="contracts-table"
                   show-toggle-columns :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns" requires-min-height>

      <!--      <template v-slot:header-buttons="{selectedItems}">-->
      <!--        <b-dropdown :text="$t('Actions')" variant="primary" class="mr-2"-->
      <!--                    v-permission="changePermission">-->
      <!--        </b-dropdown>-->
      <!--      </template>-->

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="contracts-table">
          <contract-search/>
        </search-collapse>
      </template>


      <template slot="cell(filename)" slot-scope="{item: contract}">
        <div class="d-flex justify-content-start align-items-center">
          <div v-if="$hasPermission(changePermission)">
            <b-button variant="link" class="p-0 text-left"
                      @click="onPreviewClick(contract.contract_files[0])">
              {{ contract.contract_files[0].file }}
            </b-button>

            <action-buttons :edit-permission="changePermission"
                            :delete-permission="deletePermission"
                            @viewClick="onPreviewClick(contract.contract_files[0])"
                            @editClick="onEditClick(contract)"
                            @deleteClick="onFileDelete(contract)"
                            :remove="contract.status === 'draft'"/>

          </div>
          <div v-else>
            {{ contract.contract_files[0].file }}
          </div>
        </div>
      </template>

      <template slot="cell(status)" slot-scope="{item: contract}">
        <span v-as-status="contract.status">
          {{ contract.status | removeUnderscores | toUpperCaseFirst }}
        </span>
      </template>
      <template slot="cell(signature_platform)" slot-scope="{item: contract}">
        <span v-as-status="contract.signature_platform" v-if="contract.signature_platform">
          {{ contract.signature_platform | removeUnderscores | toUpperCaseFirst }}
        </span>
      </template>
      <template slot="cell(type)" slot-scope="{item: contract}">
        <span v-if="contract.type">
          {{ contractLabelMap[contract.type] }}
        </span>
      </template>

      <template slot="cell(created_at)" slot-scope="{item: c}">
        {{ c.created_at | toDateTime }} by {{ c.created_by.display_name }}
      </template>

      <template slot="cell(comment)" slot-scope="{item: c}">
        {{ c.comment | truncate(60) }}
      </template>

      <template slot="cell(sent_at)" slot-scope="{item: c}">
        <div v-if="c.sent_by">
          {{ c.sent_at | toDateTime }} by {{ c.sent_by.display_name }}
        </div>
        <div v-else-if="c.sent_at">
          {{ c.sent_at | toDateTime }}
        </div>
      </template>

      <template slot="cell(recipients)" slot-scope="{item: c}">
        <div v-if="c.recipients">
          <div v-for="r in c.recipients" :key="`recipient-row-${c.uuid}-${r.uuid}`">
            {{ r.email }} - {{ r.display_name }}
          </div>
        </div>
      </template>

      <template slot="cell(actions)" slot-scope="{item: contract}">
        <div class="d-flex">

          <div v-permission="changePermission" v-if="contract.status === 'draft'">
            <b-tooltip :target="`file-send-${contract.uuid}`" :title="$t('Send')"/>
            <b-button :id="`file-send-${contract.uuid}`" variant="success" size="sm" class="mr-2"
                      @click="onFileSend(contract)">
              <font-awesome-icon icon="arrow-up"/>
            </b-button>
          </div>

          <template v-if="isDocusignEnabled">

            <div v-permission="changePermission" v-if="contract.status === 'sent' && isThirdPartyPlatform(contract)">
              <b-tooltip :target="`file-resend-${contract.uuid}`" :title="$t('Resend')"/>
              <b-button :id="`file-resend-${contract.uuid}`" variant="warning" size="sm" class="mr-2"
                        @click="onFileResend(contract)">
                <font-awesome-icon icon="redo"/>
              </b-button>
            </div>
          </template>


        </div>
      </template>

    </artisio-table>

    <contract-files-preview-modal @onModalSubmit="onModalSubmit" @clearData="clearData"/>
    <contract-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {CONTRACTS_TABLE_NAME, SOURCE_CONTRACT_LIST} from "../constants";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import cloneDeep from "lodash/cloneDeep";
  import ContractSearch from "@/modules/contracts/ContractSearch";
  import {ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import ContractService from "@/modules/contracts/ContractService";
  import FileUploader from "@/core/components/file-uploader/FileUploader";
  import ContractFilesPreviewModal from "@/modules/contracts/ContractFilesPreviewModal";
  import ActionButtons from "@/core/components/ams-table/ActionButtons";
  import ContractModal from "@/modules/contracts/ContractModal";

  const {mapState, mapActions} = createNamespacedHelpers('contracts');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ContractTable",
    components: {
      ContractModal,
      ActionButtons, ContractFilesPreviewModal, FileUploader, ContractSearch, SearchCollapse, ArtisioTable
    },
    props: [
      'changePermission',
      'deletePermission',
      'exportPermission',
      'customerUuid',
      'objectName',
      'objectUuid',
    ],
    data() {
      return {
        exportAttributes: {},
        files: [],
        uploadingProgressValue: 0,
        uploadingProgress: false,
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
        contractLabelMap: state => state.contractLabelMap,
      }),
      ...globalMapState([
        'contractFileTypes',
        'customerMaxContractCount',
        'customerMaxContractSizeSum',
        'contractMaxFileSize',
        'isDocusignEnabled',
      ]),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(f => f.isVisible);
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: [],
          tableName: `${CONTRACTS_TABLE_NAME}.${this.tableSource}`,
        }
      },

      fileUploaderAccept() {
        return [{formats: this.contractFileTypes, maxSize: this.contractMaxFileSize}]
      },
    },
    methods: {
      ...mapActions([
        'getTableData',
        'showModal',
        'updateToggleColumns',
        'createContracts',
        'deleteContract',
        'showContractSendModal',
        'showContractFilesPreviewModal',
        'showContractModal',
      ]),
      isThirdPartyPlatform(contract) {
        return ContractService.isThirdPartyPlatform(contract)
      },
      async onPreviewClick(contractFile) {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t(contractFile.file),
        });
        let response = await ContractService.getContractFilePdf(contractFile.uuid);
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...response.body,
            filename: this.$i18n.t(contractFile.file),
          })
        }
      },
      async onFileDelete(contract) {
        let success = await this.$confirm(this.$i18n.t('Are you sure you want to delete following contract?'))
        if (!success) return;

        let response = await this.deleteContract(contract.uuid);
        if (response.success) {
          this.getTableData()
          this.$toast('success', this.$i18n.t('Contract was successfully deleted'));
        }
      },
      async onFileSend(contract) {
        await ContractService.onFileSend(this, contract, SOURCE_CONTRACT_LIST, () => {
          this.getTableData()
        })
      },
      async onFileResend(contract) {
        await ContractService.onFileResend(this, contract)
      },
      async onItemsAdd() {
        this.showContractFilesPreviewModal({
          files: this.files,
          customerUuid: this.customerUuid
        })
      },
      async processFilesLogic(models, recipients) {
        const maxCount = this[`${this.object_name}MaxContractCount`];
        const maxSize = this[`${this.object_name}MaxContractSizeSum`];
        const filesSumSize = models.reduce((a, b) => a + b.file.size, 0) / 1000000; // Convert bytes to megabytes

        if (models.length > maxCount) {
          this.clearData();
          this.$errorDialog(
            this.$i18n.t("You can not upload more than {count} files.", {count: maxCount}),
            this.$i18n.t('Contracts')
          );
          return;
        }

        if (filesSumSize > maxSize) {
          this.clearData();
          this.$errorDialog(
            this.$i18n.t("Sum max size limit exceeded. Max size allowed {size} mbs.", {size: maxSize}),
            this.$i18n.t('Contracts')
          );
          return;
        }

        let contract_data = {
          'object_name': this.objectName,
          'object_uuid': this.objectUuid,
          'customer_uuid': this.customerUuid,
          'recipients': recipients,
        }

        let formData = new FormData();
        models.forEach(item => formData.append('files', item.file))
        models.forEach(item => formData.append('comments', item.comment ? item.comment : ''))
        formData.append('contract_data', JSON.stringify(contract_data))

        this.uploadingProgress = true;

        let response = await this.createContracts({
          formData, onUploadProgress: (progressEvent) => {
            this.uploadingProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
          }
        });

        this.clearData();
        if (response.success) {
          this.getTableData()
          this.$toast('success', this.$i18n.t('File were uploaded successfully'));
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.$errorDialog(this.$i18n.t('File could not be uploaded'));
          }
        }
        this.uploadingProgress = false;
        this.uploadingProgressValue = 0;
      },
      clearData() {
        this.files = [];
        this.$refs.fileUploader.clearInput();
      },
      onModalSubmit({files, recipients}) {
        this.processFilesLogic(files, recipients)
      },
      onEditClick(contract) {
        this.showContractModal({
          object: contract
        })
      },
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {
    }
  }
</style>

